import { normalize } from 'normalizr'
import { BEGIN, COMMIT, REVERT } from 'redux-optimist'
import { fromString as uuidFromString, v4 as uuidV4 } from 'uuid'

import graphql from 'api/graphql'
import appGraphql from 'api/app_graphql'
import grooveAPI from 'api/groove'
import { oauthTokenSelector } from 'selectors/app'
import debug from 'util/debug'
import storage from 'util/storage'
import { getTabId } from 'util/tabId'
import { getVersion } from 'util/version'
import config from 'config'

window.uuidFromString = uuidFromString

export const doRequest = (
  requestType,
  promiseFunction,
  variables,
  options = {}
) => {
  const requestId = options.requestId || uuidV4()
  const {
    optimist = null,
    optimist: {
      id: optimistId = requestId,
      payload: optimistPayload = optimist,
      entities: optimistEntities = null,
    } = {},
  } = options
  return async (dispatch, getState) => {
    if (!options.skipStarted) {
      dispatch({
        type: `${requestType}_STARTED`,
        optimist: optimist
          ? { type: BEGIN, id: optimistId, payload: optimistPayload }
          : null,
        meta: {
          requestType,
          requestId,
          requestParameters: variables,
          ...options.meta,
        },
        entities: optimistEntities,
        payload: variables,
        ...(options.includeLegacyPayload && { data: variables }), // NOTE (jscheel): For BC with older actions styles we still have sprinkled about.
      })
    }
    let successAction
    let transformedVariables = variables
    if (options.transformVariables) {
      transformedVariables = options.transformVariables(variables)
    }

    try {
      let data
      if (
        options.cacheKey &&
        config.isDevelopment &&
        storage.get('cacheRequests')
      ) {
        const lsKey = `requestCache-${options.cacheKey}`
        data = storage.get(lsKey, undefined)
        if (data === undefined) {
          data = await promiseFunction({
            variables: transformedVariables,
            dispatch,
            getState,
          })
          storage.set(lsKey, data)
        }
      } else {
        data = await promiseFunction({
          variables: transformedVariables,
          dispatch,
          getState,
        })
      }
      const transformedData = options.transformResponse
        ? options.transformResponse(data)
        : data
      const { entities } = options.normalizationSchema
        ? normalize(transformedData, options.normalizationSchema)
        : {}

      const transformedEntities = options.transformEntities
        ? options.transformEntities(entities)
        : entities

      successAction = {
        type: `${requestType}_SUCCESS`,
        entities: transformedEntities,
        optimist: optimist ? { type: COMMIT, id: optimistId } : null,
        payload: transformedData,
        ...(options.includeLegacyPayload && { data: transformedData }), // NOTE (jscheel): For BC with older actions styles we still have sprinkled about.
        meta: {
          requestId,
          requestType,
          requestParameters: transformedVariables,
          ...options.meta,
        },
      }
      if (options.onBeforeSuccessAction) {
        await options.onBeforeSuccessAction(transformedData)
      }
      dispatch(successAction)
      return transformedData
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      debug(`${options.logPrefix || 'REQUEST'}: ${requestType}_FAILED`, error)
      dispatch({
        type: `${requestType}_FAILED`,
        error: true,
        optimist: optimist ? { type: REVERT, id: optimistId } : null,
        originalAction: successAction,
        meta: {
          requestType,
          requestId,
          requestParameters: transformedVariables,
          ...options.meta,
        },
        payload: { error, successAction },
        ...(options.includeLegacyPayload && { data: { error } }), // NOTE (jscheel): For BC with older actions styles we still have sprinkled about.
      })
      // HACK (jscheel): We have a ton of legacy code that does not listen for
      // this error. However, it is useful if you need to chain a promise catch.
      // This way, you can use promise.catch if you need to by passing this in.
      if (options.throwOnError) {
        throw error
      }
      return null
    }
  }
}

export const doGraphqlRequest = (
  requestType,
  queryOrMutation,
  variables,
  options = {}
) => {
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const graphqlFunc = options.app === true ? appGraphql : graphql
      const res = await graphqlFunc(
        token,
        queryOrMutation,
        transformedVariables
      )
      return res.json.data
    },
    variables,
    {
      ...options,
      logPrefix: 'GQL',
    }
  )
}

export function doAppGraphqlRequest(
  requestType,
  queryOrMutation,
  variables,
  options
) {
  return doGraphqlRequest(requestType, queryOrMutation, variables, {
    ...options,
    app: true,
  })
}

export const doSdkRequest = (
  requestType,
  sdkPromiseFunction,
  variables = {},
  options = {}
) => {
  return doRequest(requestType, sdkPromiseFunction, variables, {
    ...options,
    logPrefix: 'SDK',
  })
}

export const doApiReadRequest = (requestType, path, params, options = {}) => {
  const requestId = options.requestId || uuidV4()
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    if (!options.skipActions)
      dispatch({
        type: `${requestType}_STARTED`,
        meta: { requestType, requestId, ...options.meta },
      })

    let successAction
    return grooveAPI
      .get(token, path, params)
      .then(res => {
        let data = res.json
        if (options.transformResponse) {
          data = options.transformResponse(data)
        }
        if (!options.skipActions) {
          successAction = {
            type: `${requestType}_SUCCESS`,
            payload: data,
            meta: {
              requestType,
              requestId,
              requestParameters: params,
              ...options.meta,
            },
          }
          dispatch(successAction)
        }
        return data
      })
      .catch(error => {
        if (!options.skipActions) {
          dispatch({
            type: `${requestType}_FAILED`,
            payload: { error, successAction },
            meta: {
              requestType,
              requestId,
              requestParameters: params,
              ...options.meta,
            },
            error: true,
          })
        }
        throw error
      })
  }
}

export const doAPIPutRequest = (requestType, path, payload, options = {}) => {
  const requestId = options.requestId || uuidV4()
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    if (!options.skipActions) {
      dispatch({
        type: `${requestType}_STARTED`,
        meta: { requestType, requestId, ...options.meta },
      })
    }

    return grooveAPI
      .put(token, path, options.params, JSON.stringify(payload))
      .then(res => {
        let data = res.json
        if (options.transformResponse) {
          data = options.transformResponse(data)
        }
        if (!options.skipActions) {
          dispatch({
            type: `${requestType}_SUCCESS`,
            payload: data,
            meta: { requestType, requestId, ...options.meta },
          })
        }
        return data
      })
      .catch(error => {
        if (!options.skipActions) {
          dispatch({
            type: `${requestType}_FAILED`,
            payload: { error },
            meta: { requestType, requestId, ...options.meta },
            error: true,
          })
        }
        throw error
      })
  }
}

export const doApiWriteRequest = (requestType, path, payload, options = {}) => {
  const requestId = options.requestId || uuidV4()
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const method = options.method || 'post'
    const body = JSON.stringify(payload)
    const params = options.params

    if (!options.skipActions) {
      dispatch({
        type: `${requestType}_STARTED`,
        meta: { requestType, requestId, ...options.meta },
      })
    }

    return grooveAPI
      .send(method, token, path, params, body)
      .then(res => {
        let data = res.json
        if (options.transformResponse) {
          data = options.transformResponse(data)
        }
        if (!options.skipActions) {
          dispatch({
            type: `${requestType}_SUCCESS`,
            payload: data,
            meta: { requestType, requestId, ...options.meta },
          })
        }
        return data
      })
      .catch(error => {
        if (!options.skipActions) {
          dispatch({
            type: `${requestType}_FAILED`,
            payload: { error },
            meta: { requestType, requestId, ...options.meta },
            error: true,
          })
        }

        throw error
      })
  }
}

export function doAttachementRequest(
  requestType,
  path,
  attachments,
  options = {}
) {
  return doRequest(
    requestType,
    async ({ getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const formData = new FormData()
      attachments.forEach(attachment => formData.append('files[]', attachment))
      return fetch(`${config.uploaderUrl}${path}`, {
        body: formData,
        cache: 'no-cache',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Request-Id': uuidV4(),
          'X-Client-Tab-Id': getTabId(),
          'X-Client-App-Version': getVersion(),
        },
        method: 'POST',
      })
    },
    {},
    options
  )
}
